import {Status as AutomationFileStatusGraphQL} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  AutomationOutputFileSpec,
  AutomationOutputFileType,
  ProcessingSummaryStatistics,
} from 'lab-automation/automation-files/LabAutomationConstants';
import {FromServerResultsTable} from 'notebook/integrations/results/ResultsTableConstants';
import * as net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

import {AutomationErrorFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationErrorFileGraphQL';
import {AutomationInputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationInputFileGraphQL';
import {AutomationOutputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationOutputFileGraphQL';
import {FromServerRunOutputSamplesTable} from '../output-samples/RunOutputSamplesTableConstants';

// TODO: Remove when clients have settled and server is sending GraphQL responses only
interface RawAutomationInputFileResponse {
  automationInputFileGql: AutomationInputFileGraphQL;
  automationOutputFileGql?: AutomationOutputFileGraphQL | null;
  errorMessage?: string | null;
  instrumentQueryId?: string | null;
}

export interface AutomationInputFileResponse {
  automationInputFile: AutomationInputFileGraphQL;
  automationOutputFile?: AutomationOutputFileGraphQL | null;
  errorMessage?: string | null;
  instrumentQueryId?: string | null;
}

function mapInputFileResponse(response: RawAutomationInputFileResponse): AutomationInputFileResponse {
  return {
    automationInputFile: response.automationInputFileGql,
    automationOutputFile: response.automationOutputFileGql || null,
    errorMessage: response.errorMessage || null,
    instrumentQueryId: response.instrumentQueryId || null,
  };
}

function mapOutputFileResponse(response: {automationOutputFileGql: AutomationOutputFileGraphQL}): {
  automationOutputFile: AutomationOutputFileGraphQL;
} {
  return {automationOutputFile: response.automationOutputFileGql};
}

function mapAutomationOutputFilesResponse(response: {
  automationOutputFilesGql: Array<AutomationOutputFileGraphQL>;
}): {automationOutputFiles: Array<AutomationOutputFileGraphQL>} {
  return {
    automationOutputFiles: response.automationOutputFilesGql,
  };
}

export function createAutomationInputFileBlob(
  assayRunId: string,
  configName: string,
  copyToOutputFile: boolean,
  username?: string | null
): Promise<AutomationInputFileResponse> {
  // With our current version of JQuery, resolving a JQueryPromise can return the specified type parameter or
  // `undefined`
  return net
    .postJSONPromise({
      url: '/1/api/assay-runs:create-automation-input-file-blob',
      data: {assayRunId, configName, copyToOutputFile, username},
    })
    .then((response) =>
      TaskRunner.resumeTask<RawAutomationInputFileResponse>(response.status_url).then((pollResponse) =>
        pollResponse != null ? mapInputFileResponse(pollResponse) : undefined
      )
    ) as Promise<AutomationInputFileResponse>;
}

export function removeBlobFromInputFile(
  runId: string,
  configName: string
): Promise<AutomationInputFileResponse> {
  return net
    .postJSONPromise({
      url: '/1/api/automation-files:remove-blob-from-automation-input-file',
      data: {
        runId,
        configName,
      },
    })
    .then((response) => mapInputFileResponse(response));
}

export function updateAutomationInputFileConnectionError(
  automationInputFileId: string,
  errorMessage: string
): Promise<{success: boolean}> {
  return net.postJSONPromise({
    url: `/1/api/automation-input-files/${automationInputFileId}/connection-error`,
    data: {errorMessage},
  });
}

export function bulkUploadAutomationOutputFileBlobs({
  runId,
  configName,
  blobOrFileIds,
  outputFileType,
  markSuccess,
  parentFileIds,
  folderId,
  locationId,
  timezone,
  skipRowsWithErrors,
}: {
  runId: string;
  configName: string;
  blobOrFileIds: Array<string>;
  outputFileType?: AutomationOutputFileType;
  markSuccess?: boolean;
  parentFileIds?: Array<string>;
  folderId?: string;
  locationId?: string;
  timezone?: string;
  skipRowsWithErrors?: boolean;
}): Promise<{automationOutputFiles: Array<AutomationOutputFileGraphQL>}> {
  return net
    .postJSONPromise({
      url: '/1/api/automation-files:bulk-upload-automation-output-file-blobs',
      data: {
        runId,
        configName,
        blobOrFileIds,
        outputFileType,
        markSuccess,
        parentFileIds,
        folderId,
        locationId,
        timezone,
        skipRowsWithErrors,
      },
    })
    .then((response) => mapAutomationOutputFilesResponse(response));
}

export function clearAutomationOutputFileBlob(
  automationOutputFileId: string
): Promise<{automationOutputFile: AutomationOutputFileGraphQL}> {
  return net
    .postJSONPromise({
      url: '/1/api/automation-files:update-automation-output-file-blob',
      data: {
        runId: null,
        configName: null,
        automationOutputFileId,
        blobId: null,
      },
    })
    .then((response) => mapOutputFileResponse(response));
}

// Legacy, only in use on one tenant where ENABLE_LAB_AUTOMATION_BULK_UPLOAD_OUTPUT_FILES is enabled
export function bulkUpdateAutomationOutputFileBlobs({
  runId,
  outputFiles,
}: {
  runId: string;
  outputFiles: Array<AutomationOutputFileSpec>;
}): Promise<{automationOutputFiles: Array<AutomationOutputFileGraphQL>}> {
  return net
    .postJSONPromise({
      url: '/1/api/automation-files:bulk-update-automation-output-file-blobs',
      data: {runId, outputFiles},
    })
    .then((response) => mapAutomationOutputFilesResponse(response));
}

export function updateAutomationOutputFileParams(
  automationOutputFileId: string,
  params: {
    folderId?: string | null;
    locationId?: string;
    timezone?: string | null;
    skipRowsWithErrors?: boolean;
  }
): Promise<{automationOutputFile: AutomationOutputFileGraphQL}> {
  return net
    .postJSONPromise({
      url: '/1/api/automation-files:update-automation-output-file-params',
      data: {
        runId: null,
        configName: null,
        automationOutputFileId,
        ...params,
      },
    })
    .then((response) => mapOutputFileResponse(response));
}

function mapOutputFileWithStructuredTablesResponse({
  automationOutputFileGql,
  structuredTables,
}: {
  automationOutputFileGql: AutomationOutputFileGraphQL;
  structuredTables: Array<FromServerRunOutputSamplesTable | FromServerResultsTable>;
}): {
  automationOutputFile: AutomationOutputFileGraphQL;
  structuredTables: Array<FromServerRunOutputSamplesTable | FromServerResultsTable>;
} {
  return {
    automationOutputFile: automationOutputFileGql,
    structuredTables,
  };
}

export function fetchAutomationOutputFileAndStructuredTables(
  automationOutputFileId: string,
  entryId?: string
): Promise<{
  automationOutputFile: AutomationOutputFileGraphQL;
  structuredTables: Array<FromServerRunOutputSamplesTable | FromServerResultsTable>;
}> {
  return net
    .postJSONPromise({
      url: `/1/api/automation-files/${automationOutputFileId}/file-and-structured-tables`,
      data: {fileId: entryId},
    })
    .then((response) => mapOutputFileWithStructuredTablesResponse(response));
}

export function getAutomationInputFileAndCopiedOutputFile(
  automationInputFileId: string,
  copiedAutomationOutputFileId?: string
): Promise<AutomationInputFileResponse> {
  return net
    .postJSONPromise({
      url: `/1/api/automation-files/${automationInputFileId}/file-and-copied-output-file`,
      data: {copiedAutomationOutputFileId},
    })
    .then((response) => mapInputFileResponse(response));
}

interface AutomationOutputFileStatusResponse {
  automationOutputFileStatus: AutomationFileStatusGraphQL;
  automationTransformFileStatuses: Array<AutomationFileStatusGraphQL>;
}

interface AutomationInputFileStatusResponse {
  automationInputFileStatus: AutomationFileStatusGraphQL;
  automationTransformFileStatuses: Array<AutomationFileStatusGraphQL>;
}

export type AutomationFileStatusResponse =
  | AutomationOutputFileStatusResponse
  | AutomationInputFileStatusResponse;

export function getAutomationFileStatus(automationFileId: string): Promise<AutomationFileStatusResponse> {
  return net.getJSONPromise(`/1/api/automation-files/${automationFileId}/status`);
}

export function startProcessingAutomationOutputFile(
  automationOutputFileId: string,
  fileId?: string
): JQueryPromise<{}> {
  return TaskRunner.runTask({
    contentType: 'application/json',
    data: JSON.stringify({assayRunId: null, configName: null, automationOutputFileId, documentId: fileId}),
    type: 'POST',
    dataType: 'json',
    url: '/1/api/assay-runs:process-automation-output-file',
  });
}

export function updateAutomationErrorFileBlob(
  runId: string,
  configName: string,
  blobId: string | null
): Promise<{automationErrorFile: AutomationErrorFileGraphQL}> {
  return net
    .postJSONPromise({
      url: '/1/api/automation-files:update-automation-error-file-blob',
      data: {
        runId,
        configName,
        blobId,
      },
    })
    .then((res) => ({automationErrorFile: res.automationErrorFileGql}));
}

export type AnyAutomationFileResponse =
  | {automationOutputFile: AutomationOutputFileGraphQL}
  | {automationInputFile: AutomationInputFileGraphQL};

export function cancelCustomTransform(automationFileId: string): Promise<AnyAutomationFileResponse> {
  return net
    .patchJSONPromise({
      url: '/1/api/automation-files:cancel-custom-transform',
      data: {automationFileId},
    })
    .then((response) =>
      response.automationOutputFile ? response : {automationInputFile: response.automationInputFileGql}
    );
}

export function getProcessingSummaryStatistics(
  automationOutputFileId: string
): Promise<{processingSummaryStatistics: ProcessingSummaryStatistics | null}> {
  return net.getJSONPromise(
    `/1/api/automation-files/${automationOutputFileId}/get-processing-summary-statistics`,
    {
      automationOutputFileId,
    }
  );
}
